import { Route } from '../router/Router';
import { Home } from '../pages/Home';
import { AboutUsPage } from '../pages/AboutUs';
import { ContactPage } from '../pages/Contact';
import { ServicesPage } from '../pages/Services';
import { NewsPage } from '../pages/News';
import { ArticleDetailPage } from '../pages/ArticleDetail';
import { CareersPage } from '../pages/Careers';
import { getCurrentWebsiteConfig } from './websites';

// Import all locale files statically
import blueskyEnTranslations from '../locales/bluesky/en.json';
import blueskyArTranslations from '../locales/bluesky/ar.json';
import emcorEnTranslations from '../locales/emcor/en.json';
import emcorArTranslations from '../locales/emcor/ar.json';
import basraskyEnTranslations from '../locales/basrasky/en.json';
import basraskyArTranslations from '../locales/basrasky/ar.json';

// Translation cache for metadata
const translationCache = new Map<string, any>([
  ['bluesky-en', blueskyEnTranslations],
  ['bluesky-ar', blueskyArTranslations],
  ['emcor-en', emcorEnTranslations],
  ['emcor-ar', emcorArTranslations],
  ['basrasky-en', basraskyEnTranslations],
  ['basrasky-ar', basraskyArTranslations],
]);

// Function to create metadata for a route
async function createMetadata(
  websiteConfig: any,
  pageKey: string,
  defaultTitle: string,
  defaultDescription: string,
  defaultKeywords: string,
  language: 'en' | 'ar' = 'en'
) {
  try {
    // Get translations from cache
    const cacheKey = `${websiteConfig.name.toLowerCase()}-${language}`;
    const localeData = translationCache.get(cacheKey);

    if (!localeData) {
      throw new Error(`No translations found for ${cacheKey}`);
    }

    // Get SEO data from locale
    const seoData = localeData.seo || {};

    // Get page-specific title if available
    const pageTitle = localeData[pageKey]?.title || defaultTitle;

    return {
      title: `${pageTitle} - ${seoData.title || websiteConfig.name}`,
      description: seoData.description || defaultDescription,
      keywords: seoData.keywords || defaultKeywords,
      type: 'website',
      imageUrl: websiteConfig.seo.ogImage,
    };
  } catch (error) {
    // Fallback to default values if locale file can't be loaded
    return {
      title: `${defaultTitle} - ${websiteConfig.name}`,
      description: defaultDescription,
      keywords: defaultKeywords,
      type: 'website',
      imageUrl: websiteConfig.seo.ogImage,
    };
  }
}

// Function to generate routes for a specific website
export function generateRoutes(): Route[] {
  const websiteConfig = getCurrentWebsiteConfig();

  return [
    // English routes
    {
      path: '/',
      component: Home,
      metaData: async () => createMetadata(
        websiteConfig,
        'home',
        'Home',
        'Welcome to our professional services',
        'home, professional, services',
        'en'
      ),
    },
    {
      path: '/en',
      component: Home,
      metaData: async () => createMetadata(
        websiteConfig,
        'home',
        'Home',
        'Welcome to our professional services',
        'home, professional, services',
        'en'
      ),
    },
    {
      path: '/about',
      component: AboutUsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'about',
        'About Us',
        'Learn more about our company and values',
        'about us, company, values, mission',
        'en'
      ),
    },
    {
      path: '/en/about',
      component: AboutUsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'about',
        'About Us',
        'Learn more about our company and values',
        'about us, company, values, mission',
        'en'
      ),
    },
    {
      path: '/about-us',
      component: AboutUsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'about',
        'About Us',
        'Learn more about our company and values',
        'about us, company, values, mission',
        'en'
      ),
    },
    {
      path: '/en/about-us',
      component: AboutUsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'about',
        'About Us',
        'Learn more about our company and values',
        'about us, company, values, mission',
        'en'
      ),
    },
    {
      path: '/contact',
      component: ContactPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'contact',
        'Contact Us',
        'Get in touch with us for inquiries and support',
        'contact, support, inquiry, get quote',
        'en'
      ),
    },
    {
      path: '/en/contact',
      component: ContactPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'contact',
        'Contact Us',
        'Get in touch with us for inquiries and support',
        'contact, support, inquiry, get quote',
        'en'
      ),
    },
    {
      path: '/services',
      component: ServicesPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'services',
        'Services',
        'Our professional services and solutions',
        'services, professional, energy, commodities, trading',
        'en'
      ),
    },
    {
      path: '/en/services',
      component: ServicesPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'services',
        'Services',
        'Our professional services and solutions',
        'services, professional, energy, commodities, trading',
        'en'
      ),
    },
    {
      path: '/news',
      component: NewsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'news',
        'News & Updates',
        'Latest news, industry insights, and company updates',
        'news, updates, industry, insights, press releases',
        'en'
      ),
    },
    {
      path: '/news/:articleId',
      component: ArticleDetailPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'article',
        'Article',
        'Read the full article',
        'article, news, read',
        'en'
      ),
    },
    {
      path: '/en/news',
      component: NewsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'news',
        'News & Updates',
        'Latest news, industry insights, and company updates',
        'news, updates, industry, insights, press releases',
        'en'
      ),
    },
    {
      path: '/en/news/:articleId',
      component: ArticleDetailPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'article',
        'Article',
        'Read the full article',
        'article, news, read',
        'en'
      ),
    },
    {
      path: '/careers',
      component: CareersPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'careers',
        'Careers',
        'Join our team and explore career opportunities',
        'careers, jobs, employment, opportunities, hiring',
        'en'
      ),
    },
    {
      path: '/en/careers',
      component: CareersPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'careers',
        'Careers',
        'Join our team and explore career opportunities',
        'careers, jobs, employment, opportunities, hiring',
        'en'
      ),
    },

    // Arabic routes
    {
      path: '/ar',
      component: Home,
      metaData: async () => createMetadata(
        websiteConfig,
        'home',
        'الرئيسية',
        'مرحباً بك في خدماتنا المهنية',
        'الرئيسية, مهني, خدمات, العربية',
        'ar'
      ),
    },
    {
      path: '/ar/about',
      component: AboutUsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'about',
        'من نحن',
        'اعرف المزيد عن شركتنا وقيمنا',
        'من نحن, شركة, قيم, مهمة, العربية',
        'ar'
      ),
    },
    {
      path: '/ar/about-us',
      component: AboutUsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'about',
        'من نحن',
        'اعرف المزيد عن شركتنا وقيمنا',
        'من نحن, شركة, قيم, مهمة, العربية',
        'ar'
      ),
    },
    {
      path: '/ar/contact',
      component: ContactPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'contact',
        'اتصل بنا',
        'تواصل معنا للاستفسارات والدعم',
        'اتصل بنا, دعم, استفسار, احصل على عرض سعر, العربية',
        'ar'
      ),
    },
    {
      path: '/ar/services',
      component: ServicesPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'services',
        'الخدمات',
        'خدماتنا المهنية وحلولنا',
        'خدمات, مهني, طاقة, سلع, تجارة, العربية',
        'ar'
      ),
    },
    {
      path: '/ar/news',
      component: NewsPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'news',
        'الأخبار والتحديثات',
        'أحدث الأخبار ورؤى الصناعة وتحديثات الشركة',
        'أخبار, تحديثات, صناعة, رؤى, بيانات صحفية, العربية',
        'ar'
      ),
    },
    {
      path: '/ar/news/:articleId',
      component: ArticleDetailPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'article',
        'مقال',
        'اقرأ المقال كاملاً',
        'مقال, أخبار, قراءة, العربية',
        'ar'
      ),
    },
    {
      path: '/ar/careers',
      component: CareersPage,
      metaData: async () => createMetadata(
        websiteConfig,
        'careers',
        'الوظائف',
        'انضم إلى فريقنا واستكشف فرص العمل',
        'وظائف, عمل, توظيف, فرص, تعيين, العربية',
        'ar'
      ),
    },
  ];
}

// Export the generated routes
export const routes: Route[] = generateRoutes();
